// ProductSelector.js - Two cascading accordions: Category -> Models (ignoring families)
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { createPortal } from "react-dom"
import gsap from "gsap"
import { fetchProductStructure, fetchFullProductStructure } from "./api"
import { getTranslations } from "./components/translations"
import placeholder from "./assets/placeholder.jpg"
import { FaSpinner } from "react-icons/fa"

const ITEMS_PER_BATCH = 15
const FETCH_DEBOUNCE_TIME = 500

// Hover image config
const IMAGE_SIZE = 200
const IMAGE_HEIGHT = IMAGE_SIZE // Square image
const IMAGE_GAP = 15 // Distance from cursor
const IMAGE_POSITION = "right" // "top", "bottom", "left", "right"
const FOLLOW_SPEED = 0.2
const MAX_ROTATION = 1

const ProductSelector = ({
  products,
  selectedFamily,
  onFamilyChange,
  selectedProduct,
  onProductChange,
  onOpenContactModal,
  language = "it",
  openAccordion,
  onAccordionOpen,
}) => {
  // Accordion states
  const [categoryOpen, setCategoryOpenInternal] = useState(false)
  const [modelOpen, setModelOpenInternal] = useState(false)

  // Sync with external control (mobile: only 1 open at a time)
  useEffect(() => {
    if (openAccordion !== undefined) {
      setCategoryOpenInternal(openAccordion === "category")
      setModelOpenInternal(openAccordion === "model")
    }
  }, [openAccordion])

  const setCategoryOpen = (val) => {
    setCategoryOpenInternal(val)
    if (val && onAccordionOpen) onAccordionOpen("category")
  }
  const setModelOpen = (val) => {
    setModelOpenInternal(val)
    if (val && onAccordionOpen) onAccordionOpen("model")
  }

  // Content heights for animations
  const [categoryHeight, setCategoryHeight] = useState(0)
  const [modelHeight, setModelHeight] = useState(0)

  // Data states
  const [productStructure, setProductStructure] = useState({})
  const [categoryData, setCategoryData] = useState({})
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [loading, setLoading] = useState(false)
  const [demoFamilies, setDemoFamilies] = useState(new Set()) // Famiglie con demo: true

  // Model accordion states
  const [previewImages, setPreviewImages] = useState({})
  const [loadingStates, setLoadingStates] = useState({})
  const [failedImages, setFailedImages] = useState({})
  const [visibleProducts, setVisibleProducts] = useState([])

  // Hover image state
  const [currentHoverIndex, setCurrentHoverIndex] = useState(-1)

  // Refs
  const categoryContentRef = useRef(null)
  const modelContentRef = useRef(null)
  const modelScrollRef = useRef(null)
  const fetchQueueRef = useRef(new Set())
  const fetchTimeoutRef = useRef(null)
  const prevCategoryRef = useRef(selectedCategory)

  // GSAP hover animation refs
  const floatingContainerRef = useRef(null)
  const posRef = useRef({ x: 0, y: 0 })
  const prevPosRef = useRef({ x: 0, y: 0 })
  const velocityRef = useRef({ x: 0 })
  const rotationRef = useRef(0)
  const targetRotationRef = useRef(0)
  const rafIdRef = useRef(null)
  const lastTimeRef = useRef(0)
  const isHoveringRef = useRef(false)
  const xToRef = useRef(null)
  const yToRef = useRef(null)

  const translations = getTranslations(language)

  // Initialize GSAP hover animation
  useEffect(() => {
    // Skip on touch devices
    if ("ontouchstart" in window || navigator.maxTouchPoints > 0) return

    // Initialize quickTo functions once container is available
    const initGsap = () => {
      if (!floatingContainerRef.current) return

      // Set initial state
      gsap.set(floatingContainerRef.current, {
        opacity: 0,
        scale: 0,
        force3D: true,
      })

      // Create quickTo functions for smooth cursor following
      xToRef.current = gsap.quickTo(floatingContainerRef.current, "x", {
        duration: FOLLOW_SPEED,
        ease: "power3.out",
      })
      yToRef.current = gsap.quickTo(floatingContainerRef.current, "y", {
        duration: FOLLOW_SPEED,
        ease: "power3.out",
      })

      // Velocity tracking loop for rotation
      const track = (timestamp) => {
        if (!lastTimeRef.current) lastTimeRef.current = timestamp
        const deltaTime = timestamp - lastTimeRef.current
        lastTimeRef.current = timestamp

        const dx = posRef.current.x - prevPosRef.current.x

        if (deltaTime > 0) {
          velocityRef.current.x = velocityRef.current.x * 0.7 + dx * 0.3
        }

        prevPosRef.current.x = posRef.current.x
        prevPosRef.current.y = posRef.current.y

        // Calculate target rotation based on horizontal velocity
        const velocityInfluence = velocityRef.current.x * 0.5
        targetRotationRef.current = Math.max(
          -MAX_ROTATION,
          Math.min(MAX_ROTATION, velocityInfluence),
        )

        // Interpolate rotation
        if (isHoveringRef.current) {
          rotationRef.current +=
            (targetRotationRef.current - rotationRef.current) * 0.2
        } else {
          rotationRef.current *= 0.92
        }

        // Apply rotation
        if (floatingContainerRef.current) {
          gsap.set(floatingContainerRef.current, {
            rotation: rotationRef.current,
          })
        }

        rafIdRef.current = requestAnimationFrame(track)
      }

      rafIdRef.current = requestAnimationFrame(track)
    }

    // Delay init to ensure portal has rendered
    const timer = setTimeout(initGsap, 100)

    return () => {
      clearTimeout(timer)
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
      }
      if (floatingContainerRef.current) {
        gsap.killTweensOf(floatingContainerRef.current)
      }
    }
  }, [])

  // Animation duration constant
  const ANIMATION_DURATION = 0.5

  // Calculate position based on IMAGE_POSITION config
  const getImagePosition = useCallback((clientX, clientY) => {
    switch (IMAGE_POSITION) {
      case "top":
        return {
          x: clientX - IMAGE_SIZE / 2,
          y: clientY - IMAGE_HEIGHT - IMAGE_GAP,
        }
      case "left":
        return {
          x: clientX - IMAGE_SIZE - IMAGE_GAP,
          y: clientY - IMAGE_HEIGHT / 2,
        }
      case "right":
        return {
          x: clientX + IMAGE_GAP,
          y: clientY - IMAGE_HEIGHT / 2,
        }
      case "bottom":
      default:
        return {
          x: clientX - IMAGE_SIZE / 2,
          y: clientY + IMAGE_GAP,
        }
    }
  }, [])

  // Show floating image (only on non-touch devices) - animation.com style
  const showFloatingImage = useCallback(
    (index, e) => {
      // Skip on touch devices
      if ("ontouchstart" in window || navigator.maxTouchPoints > 0) return
      if (!floatingContainerRef.current) return

      const wasHovering = isHoveringRef.current

      setCurrentHoverIndex(index)
      isHoveringRef.current = true

      // Update position based on config
      const pos = getImagePosition(e.clientX, e.clientY)
      posRef.current.x = pos.x
      posRef.current.y = pos.y

      // Show container if not already visible
      if (!wasHovering) {
        // Set initial position before showing
        gsap.set(floatingContainerRef.current, {
          x: posRef.current.x,
          y: posRef.current.y,
        })

        gsap.fromTo(
          floatingContainerRef.current,
          { opacity: 0, scale: 0.5 },
          {
            opacity: 1,
            scale: 1,
            duration: ANIMATION_DURATION * 0.6,
            ease: "back.out(1.7)",
            force3D: true,
          },
        )
      }

      // Hide all other images instantly (animation.com style)
      const allImages =
        floatingContainerRef.current.querySelectorAll("[data-index]")
      allImages.forEach((img, i) => {
        if (img && parseInt(img.getAttribute("data-index")) !== index) {
          gsap.killTweensOf(img)
          gsap.set(img, { opacity: 0, scale: 0.5 })
        }
      })

      // Show current image with back.out easing
      const currentImageEl = floatingContainerRef.current.querySelector(
        `[data-index="${index}"]`,
      )
      if (currentImageEl) {
        gsap.killTweensOf(currentImageEl)
        gsap.fromTo(
          currentImageEl,
          { opacity: 0, scale: 0.5 },
          {
            opacity: 1,
            scale: 1,
            duration: ANIMATION_DURATION * 0.5,
            ease: "back.out(1.4)",
            force3D: true,
          },
        )
      }
    },
    [visibleProducts, previewImages, getImagePosition],
  )

  // Hide floating image - animation.com style
  const hideFloatingImage = useCallback(() => {
    isHoveringRef.current = false
    setCurrentHoverIndex(-1)

    if (floatingContainerRef.current) {
      // Hide container with scale to 0
      gsap.to(floatingContainerRef.current, {
        opacity: 0,
        scale: 0,
        duration: ANIMATION_DURATION * 0.6,
        ease: "expo.out",
        force3D: true,
      })

      // Hide all images
      const allImages =
        floatingContainerRef.current.querySelectorAll("[data-index]")
      allImages.forEach((img) => {
        if (img) {
          gsap.to(img, {
            opacity: 0,
            scale: 0,
            duration: ANIMATION_DURATION * 0.4,
            ease: "expo.out",
            force3D: true,
          })
        }
      })
    }
  }, [])

  // Move floating image
  const moveFloatingImage = useCallback(
    (e) => {
      if (!isHoveringRef.current) return

      const pos = getImagePosition(e.clientX, e.clientY)
      posRef.current.x = pos.x
      posRef.current.y = pos.y

      xToRef.current?.(posRef.current.x)
      yToRef.current?.(posRef.current.y)
    },
    [getImagePosition],
  )

  // Load product structure on mount
  useEffect(() => {
    loadProductStructure()
  }, [])

  // Update selectedCategory when selectedProduct changes (after initial load)
  useEffect(() => {
    if (selectedProduct && Object.keys(productStructure).length > 0) {
      const productFamily = selectedProduct._family || selectedProduct.family
      if (productFamily) {
        for (const category of Object.keys(productStructure)) {
          if (productStructure[category]?.includes(productFamily)) {
            if (category !== selectedCategory) {
              setSelectedCategory(category)
            }
            break
          }
        }
      }
    }
  }, [selectedProduct, productStructure])

  // Calculate content heights
  useEffect(() => {
    if (categoryOpen && categoryContentRef.current) {
      setCategoryHeight(categoryContentRef.current.scrollHeight)
    } else {
      setCategoryHeight(0)
    }
  }, [categoryOpen, categories])

  useEffect(() => {
    if (modelOpen && modelContentRef.current) {
      setModelHeight(modelContentRef.current.scrollHeight)
    } else {
      setModelHeight(0)
    }
  }, [modelOpen, visibleProducts])

  // Products filtered by selected category (ignoring families)
  const categoryProducts = useMemo(() => {
    if (!selectedCategory || !productStructure[selectedCategory]) return []

    // Get all families in this category
    const familiesInCategory = productStructure[selectedCategory] || []

    // Get all products that belong to any family in this category
    return products.filter((product) => {
      const productFamily = product._family || product.family || ""
      return familiesInCategory.includes(productFamily)
    })
  }, [products, selectedCategory, productStructure])

  // When category changes, reset visible products and open model accordion
  useEffect(() => {
    if (selectedCategory && selectedCategory !== prevCategoryRef.current) {
      setVisibleProducts([])
      fetchQueueRef.current.clear()
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current)
      }
      setModelOpen(true)
      prevCategoryRef.current = selectedCategory
    }
  }, [selectedCategory])

  // Load initial products when model accordion opens
  useEffect(() => {
    if (
      modelOpen &&
      categoryProducts.length > 0 &&
      visibleProducts.length === 0
    ) {
      const initialBatch = categoryProducts.slice(0, ITEMS_PER_BATCH)
      setVisibleProducts(initialBatch)
      initialBatch.forEach((product) => {
        if (!previewImages[product.code] && !failedImages[product.code]) {
          queueProductFetch(product)
        }
      })
    }
  }, [modelOpen, categoryProducts])

  // Load product structure
  const loadProductStructure = async () => {
    setLoading(true)
    try {
      const structure = await fetchProductStructure()
      setProductStructure(structure)

      const categoryNames = Object.keys(structure)
      setCategories(categoryNames)

      // Get category data for translations and demo families
      let categoriesData = {}
      const demoFamiliesSet = new Set()
      try {
        // Always fetch full structure to get demo families info
        const fullProductsData = await fetchFullProductStructure()
        if (fullProductsData?.categories) {
          categoriesData = fullProductsData.categories
          // Extract demo families from full structure
          Object.values(fullProductsData.categories).forEach((category) => {
            if (category.families) {
              Object.entries(category.families).forEach(
                ([familyName, familyData]) => {
                  if (familyData.demo === true) {
                    demoFamiliesSet.add(familyName)
                  }
                },
              )
            }
          })
        }
        setDemoFamilies(demoFamiliesSet)
      } catch (err) {
        console.error("Could not load full categories data:", err)
      }

      const catData = {}
      categoryNames.forEach((categoryName) => {
        catData[categoryName] = categoriesData[categoryName] || {
          name: categoryName,
        }
      })
      setCategoryData(catData)

      // Set initial selected category based on selected product's family
      if (selectedProduct) {
        const productFamily = selectedProduct._family || selectedProduct.family
        if (productFamily) {
          for (const category of categoryNames) {
            if (structure[category]?.includes(productFamily)) {
              setSelectedCategory(category)
              break
            }
          }
        }
      }
    } catch (error) {
      console.error("Failed to load product structure:", error)
    } finally {
      setLoading(false)
    }
  }

  // Get localized category name
  const getCategoryName = (categoryKey) => {
    const catData = categoryData[categoryKey]
    if (catData?.lang?.[language]) {
      return catData.lang[language]
    }
    return categoryKey
  }

  // Get localized product name
  const getLocalizedProductName = useCallback(
    (product) => {
      if (product?.lang?.[language]) {
        return product.lang[language]
      }
      return product?.name || ""
    },
    [language],
  )

  // Check if a product is a demo (belongs to a demo family)
  const isProductDemo = useCallback(
    (product) => {
      const productFamily = product._family || product.family || ""
      return demoFamilies.has(productFamily)
    },
    [demoFamilies],
  )

  // Handle category selection - close category, open model
  const handleCategorySelect = (category) => {
    setSelectedCategory(category)
    setCategoryOpen(false)
    setVisibleProducts([]) // Reset products
    // Model will open via useEffect when selectedCategory changes
  }

  // Handle model selection - close model (or open contact modal for demo products)
  const handleModelSelect = (e, product) => {
    e.preventDefault()
    e.stopPropagation()
    hideFloatingImage()

    // If product is demo, open contact modal instead of selecting
    if (isProductDemo(product)) {
      if (onOpenContactModal) {
        onOpenContactModal(product)
      }
      return
    }

    if (product.code !== selectedProduct?.code) {
      // Also update the family for compatibility
      const productFamily = product._family || product.family
      if (productFamily && productFamily !== selectedFamily) {
        onFamilyChange(productFamily)
      }
      onProductChange(product.code)
      setTimeout(() => setModelOpen(false), 50)
    }
  }

  // Fetch product image
  const fetchProductImage = useCallback(
    async (product) => {
      if (
        !product?.code ||
        loadingStates[product.code] ||
        previewImages[product.code] ||
        failedImages[product.code]
      ) {
        return
      }

      setLoadingStates((prev) => ({ ...prev, [product.code]: true }))

      try {
        const productUrl = `https://emersya.com/jsShowcase/${product.code}?container=emersyaIframe`
        const response = await fetch(productUrl)
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`)
        const data = await response.text()
        const viewerPreviewImageMatch = data.match(
          /src="(https:\/\/cdn\.emersya\.com\/cl\/[^"]+\/preview\.png\?date=[^"]+)"/,
        )

        if (viewerPreviewImageMatch) {
          setPreviewImages((prev) => ({
            ...prev,
            [product.code]: viewerPreviewImageMatch[1],
          }))
        } else {
          setFailedImages((prev) => ({ ...prev, [product.code]: true }))
        }
      } catch (error) {
        console.warn(
          `Failed to fetch preview for ${product.code}:`,
          error.message,
        )
        setFailedImages((prev) => ({ ...prev, [product.code]: true }))
      } finally {
        setLoadingStates((prev) => {
          const newState = { ...prev }
          delete newState[product.code]
          return newState
        })
      }
    },
    [loadingStates, previewImages, failedImages],
  )

  // Process fetch queue
  const processFetchQueue = useCallback(() => {
    const queue = Array.from(fetchQueueRef.current)
    fetchQueueRef.current.clear()
    queue.forEach((product) => fetchProductImage(product))
  }, [fetchProductImage])

  // Queue product fetch
  const queueProductFetch = useCallback(
    (product) => {
      if (!product?.code) return
      fetchQueueRef.current.add(product)
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current)
      }
      fetchTimeoutRef.current = setTimeout(
        () => processFetchQueue(),
        FETCH_DEBOUNCE_TIME,
      )
    },
    [processFetchQueue],
  )

  return (
    <div>
      {/* ACCORDION 1: CATEGORY */}
      <div className="ay-border-b ay-border-gray-300">
        <button
          onClick={() => setCategoryOpen(!categoryOpen)}
          className="ay-w-full ay-flex ay-items-end ay-py-4 ay-px-0 ay-transition-colors"
        >
          <div className="ay-flex ay-w-1/3 md:ay-w-1/5 ay-items-end ay-pb-0.5">
            <span className="ay-text-[12px] ay-font-serif ay-font-bold ay-text-gray-600 ay-uppercase ay-tracking-wider ay-text-left">
              CATEGORIA
            </span>
          </div>
          <div className="ay-flex ay-w-2/3 md:ay-w-4/5 ay-items-end ay-justify-between">
            <span
              className={`ay-text-base md:ay-text-xl ay-leading-5 ay-font-sans ay-font-normal ${
                selectedCategory ? "ay-text-gray-900" : "ay-text-gray-400"
              }`}
            >
              {selectedCategory
                ? getCategoryName(selectedCategory)
                : translations.selectCategory || "Seleziona categoria"}
            </span>
            <svg
              className={`ay-w-4 ay-h-4 ay-text-gray-400 ay-transition-transform ay-duration-300 ${
                categoryOpen ? "ay-rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>

        <div
          className="ay-overflow-hidden ay-transition-[max-height] ay-duration-300 ay-ease-in-out"
          style={{ maxHeight: `${categoryHeight}px` }}
        >
          <div className="ay-flex">
            <div className="ay-w-1/3 md:ay-w-1/5"></div>
            <div className="ay-w-2/3 md:ay-w-4/5">
              <div
                ref={categoryContentRef}
                className="ay-bg-white ay-max-h-[60vh] ay-overflow-y-auto"
              >
                {loading ? (
                  <div className="ay-py-4 ay-text-center">
                    <FaSpinner className="ay-animate-spin ay-text-gray-400 ay-mx-auto" />
                  </div>
                ) : (
                  <div className="ay-flex ay-flex-col ay-overflow-x-hidden">
                    {categories.map((category) => (
                      <button
                        key={category}
                        onClick={() => handleCategorySelect(category)}
                        className={`ay-w-full ay-text-left ay-py-0.5 md:ay-py-3 ay-pl-2 ay-font-sans ay-font-bold ay-text-base md:ay-text-[27px] ay-leading-relaxed md:ay-leading-normal ay-transition-[color,transform] ay-duration-200 ay-ease-out ay-origin-left ${
                          selectedCategory === category
                            ? "ay-text-gray-500 ay-skew-x-[-4deg]"
                            : "ay-text-gray-700 hover:ay-text-gray-500 hover:ay-skew-x-[-4deg]"
                        }`}
                      >
                        {getCategoryName(category)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ACCORDION 2: MODEL (directly shows products from category, ignoring families) */}
      <div className="ay-border-b ay-border-gray-300">
        <button
          onClick={() => setModelOpen(!modelOpen)}
          className="ay-w-full ay-flex ay-items-end ay-py-4 ay-px-0 ay-transition-colors"
        >
          <div className="ay-flex ay-w-1/3 md:ay-w-1/5 ay-items-end ay-pb-0.5">
            <span className="ay-text-[12px] ay-font-serif ay-font-bold ay-text-gray-600 ay-uppercase ay-tracking-wider ay-text-left">
              MODELLO
            </span>
          </div>
          <div className="ay-flex ay-w-2/3 md:ay-w-4/5 ay-items-end ay-justify-between">
            <span
              className={`ay-text-base md:ay-text-xl ay-leading-5 ay-font-sans ay-font-normal ${
                selectedProduct ? "ay-text-gray-900" : "ay-text-gray-400"
              }`}
            >
              {selectedProduct
                ? getLocalizedProductName(selectedProduct)
                : translations.selectModel || "Seleziona modello"}
            </span>
            <svg
              className={`ay-w-4 ay-h-4 ay-text-gray-400 ay-transition-transform ay-duration-300 ${
                modelOpen ? "ay-rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>

        <div
          className="ay-overflow-hidden ay-transition-[max-height] ay-duration-300 ay-ease-in-out"
          style={{ maxHeight: `${modelHeight}px` }}
        >
          <div className="ay-flex">
            <div className="ay-w-1/3 md:ay-w-1/5"></div>
            <div className="ay-w-2/3 md:ay-w-4/5">
              <div
                ref={modelContentRef}
                className="ay-bg-white ay-max-h-[60vh] ay-overflow-y-auto"
              >
                {categoryProducts.length === 0 ? (
                  <div className="ay-py-4 ay-text-center ay-text-sm ay-text-gray-500">
                    {selectedCategory
                      ? "Nessun modello disponibile"
                      : "Seleziona prima una categoria"}
                  </div>
                ) : (
                  <div
                    ref={modelScrollRef}
                    className="ay-flex ay-flex-col ay-overflow-x-hidden"
                    onMouseMove={moveFloatingImage}
                    onMouseLeave={hideFloatingImage}
                  >
                    {visibleProducts.map((product, index) => {
                      const isDemo = isProductDemo(product)
                      return (
                        <button
                          key={product.code}
                          onClick={(e) => handleModelSelect(e, product)}
                          onMouseEnter={(e) => {
                            if (isDemo) {
                              hideFloatingImage()
                            } else {
                              showFloatingImage(index, e)
                            }
                          }}
                          className="ay-group/model ay-flex ay-items-center ay-gap-3 ay-py-1.5 md:ay-py-3 ay-pl-2 ay-text-left ay-border-b ay-border-gray-200 last:ay-border-b-0 ay-relative ay-overflow-hidden"
                        >
                          {/* Thumbnail */}
                          <div className="ay-w-10 ay-h-10 ay-flex-shrink-0 ay-bg-gray-100 ay-flex ay-items-center ay-justify-center ay-overflow-hidden ay-z-10">
                            {loadingStates[product.code] ? (
                              <FaSpinner className="ay-animate-spin ay-text-sm ay-text-gray-400" />
                            ) : previewImages[product.code] ? (
                              <img
                                src={previewImages[product.code]}
                                alt={getLocalizedProductName(product)}
                                className="ay-w-full ay-h-full ay-object-cover"
                              />
                            ) : (
                              <img
                                src={placeholder}
                                alt={getLocalizedProductName(product)}
                                className="ay-w-full ay-h-full ay-object-cover ay-opacity-40"
                              />
                            )}
                          </div>

                          {/* Name - with opacity transition for demo products */}
                          <span
                            className={`ay-font-sans ay-font-bold ay-text-base md:ay-text-[27px] ay-flex-1 ay-transition-all ay-mt-2 ay-duration-300 ay-ease-out ay-origin-left ${
                              selectedProduct?.code === product.code
                                ? "ay-text-gray-500 ay-skew-x-[-4deg]"
                                : isDemo
                                  ? "ay-text-gray-700 group-hover/model:ay-opacity-30"
                                  : "ay-text-gray-700 group-hover/model:ay-text-gray-500 group-hover/model:ay-skew-x-[-4deg]"
                            }`}
                          >
                            {getLocalizedProductName(product)}
                          </span>

                          {/* Demo button - slides in from right on hover */}
                          {isDemo && (
                            <div className="ay-absolute ay-inset-y-0 ay-right-0 ay-left-14 ay-flex ay-items-center ay-justify-end ay-pr-2 ay-translate-x-full group-hover/model:ay-translate-x-0 ay-transition-transform ay-duration-300 ay-ease-out">
                              <span className="ay-font-serif ay-text-[11px] ay-font-medium ay-text-white ay-bg-gray-700 ay-px-4 ay-py-2 ay-rounded-full ay-uppercase ay-tracking-widest ay-whitespace-nowrap hover:ay-bg-gray-800 ay-transition-colors">
                                {translations.requestDemo || "Richiedi demo"}
                              </span>
                            </div>
                          )}

                          {/* Check */}
                          {selectedProduct?.code === product.code && !isDemo && (
                            <svg
                              className="ay-w-4 ay-h-4 ay-text-gray-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                        </button>
                      )
                    })}

                    {/* Load more trigger */}
                    {visibleProducts.length < categoryProducts.length && (
                      <button
                        onClick={() => {
                          const currentLength = visibleProducts.length
                          const nextBatch = categoryProducts.slice(
                            currentLength,
                            currentLength + ITEMS_PER_BATCH,
                          )
                          setVisibleProducts((prev) => [...prev, ...nextBatch])
                          nextBatch.forEach((product) => {
                            if (
                              !previewImages[product.code] &&
                              !failedImages[product.code]
                            ) {
                              queueProductFetch(product)
                            }
                          })
                        }}
                        className="ay-py-3 ay-text-sm ay-text-gray-500 hover:ay-text-gray-700 ay-transition-colors ay-text-center"
                      >
                        Carica altri...
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating hover image - GSAP controlled with stacked images (animation.com style) */}
      {createPortal(
        <div
          ref={floatingContainerRef}
          className="ay-hidden md:ay-block"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: IMAGE_SIZE,
            height: IMAGE_HEIGHT,
            pointerEvents: "none",
            zIndex: 99999,
            borderRadius: "6px",
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            backgroundColor: "#f5f5f5",
            willChange: "transform, opacity",
            opacity: 0,
            transform: "scale(0)",
          }}
        >
          {/* Stack all product images */}
          {visibleProducts.map((product, index) => (
            <img
              key={product.code}
              data-index={index}
              src={previewImages[product.code] || placeholder}
              alt=""
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                opacity: 0,
                willChange: "transform, opacity",
                transform: "scale(0)",
              }}
            />
          ))}
        </div>,
        document.body,
      )}
    </div>
  )
}

export default ProductSelector
